import { Box, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import Panel from './Panel';
import tokens from '../shared/theme/tokens';

/**
 * Themed data table inside a Panel surface. Header styling comes from the
 * theme's MuiTableCell head override; this wrapper standardizes loading,
 * empty state, row clicks, and the footer (pagination) slot.
 *
 * Props:
 * - columns: [{ key, header, render?(row), sx? }]
 * - rows: array of row objects
 * - rowKey(row): unique key
 * - onRowClick(row): optional; adds pointer + hover
 * - loading: show a centered spinner row
 * - emptyContent: node shown when rows is empty (and not loading)
 * - footer: node rendered under the table (pagination)
 * - minWidth: table min width (default 650)
 */
export default function DataTable({ columns, rows, rowKey, onRowClick, loading, emptyContent, footer, minWidth = 650 }) {
  return (
    <Panel padding={0} sx={{ maxWidth: '100%' }}>
      <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
        <Table sx={{ minWidth }}>
          <TableHead>
            <TableRow>
              {columns.map(col => (
                <TableCell key={col.key} sx={col.sx}>
                  {col.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ textAlign: 'center', py: 8 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : rows.length > 0 ? (
              rows.map(row => (
                <TableRow
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={{
                    '&:last-child td': { borderBottom: 0 },
                    ...(onRowClick && {
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: tokens.colors.hover },
                    }),
                  }}
                >
                  {columns.map(col => (
                    <TableCell key={col.key} sx={{ py: 1.75, ...col.sx }}>
                      {col.render ? col.render(row) : row[col.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                  {emptyContent}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      {footer && <Box sx={{ borderTop: `1px solid ${tokens.colors.border}` }}>{footer}</Box>}
    </Panel>
  );
}
